import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment-timezone';
import React, { useCallback, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AutoCompleteGoogle from 'src/components/AutoCompleteGoogle/AutoCompleteGoogle';
import ButtonCmp from 'src/components/ButtonCmp';
import CommonModal from 'src/components/CommonModal';
import DateSelect from 'src/components/DateSelect/DateSelect';
import InputText from 'src/components/InputText/InputText';
import SelectBox from 'src/components/SelectBox/SelectBox';
import CreateViewModal from 'src/components/TableTabButton/CreateViewModal';
import { ORDER_STATUS } from 'src/constants/common';
import { useAddressDetails } from 'src/hooks/useAddressDetails';
import { IListOption } from 'src/interface/common';
import { RootState } from 'src/redux/store';
import { createDateWithoutTimezoneConversion, getDateWithSuffixFormat, getFormattedDateIgnoringTime } from 'src/utils/CommonFunctions';
import * as Yup from 'yup';

import useGoogleMapsScript from '../hooks/useGoogleMapsScript';
import useTrackViews from '../hooks/useTrackViews';
import { buildAppliedTrackView, isTrackViewChanged, makeDefaultTrackViewParamsPatch } from '../track.helpers';
import { ITrackParams } from '../track.interface';
import { trackParams as trackParamsSelector } from '../track.slice';
import { normalizeTrackSearchQuery } from '../track.utils';

export interface ITrackFilterForm {
    refSearch: string;
    shipperId: string | null;
    consigneeId: string | null;
    status: string | null;
    serviceType: string | null;
    startDate: Date | string | null;
    endDate: Date | string | null;
    originSearch: string;
    destinationSearch: string;
}

interface IProps {
    handleClose: () => void;
    onApply: (data: ITrackFilterForm) => void;
    defaultValues?: Partial<ITrackFilterForm>;
    // How the modal was opened: the "+" / Add Filters button → 'create' (Save View creates a
    // new view while the cap allows); the Overview card's Edit View → 'edit' (Edit View
    // overwrites the active saved view). Mirrors WAL's AdvanceFilterModal viewMode.
    viewMode?: 'create' | 'edit';
}

const STAGE_OPTIONS = [
    { value: ORDER_STATUS.DISPATCHED, label: 'Pickups' },
    { value: ORDER_STATUS.IN_TRANSIT, label: 'In Transit' },
    { value: ORDER_STATUS.DELIVERED, label: 'Deliveries' },
];

const SERVICE_TYPE_OPTIONS = [
    { value: 'ftl', label: 'FTL' },
    { value: 'ltl', label: 'LTL' },
];

const DEFAULT_VALUES: ITrackFilterForm = {
    refSearch: '',
    shipperId: null,
    consigneeId: null,
    status: 'dispatched',
    serviceType: null,
    startDate: null,
    endDate: null,
    originSearch: '',
    destinationSearch: '',
};

const validationSchema = Yup.object().shape({
    endDate: Yup.mixed()
        .nullable()
        .test('end-after-start', 'End date must be after start date', function (endDate) {
            const { startDate } = this.parent;

            if (!startDate || !endDate) return true;

            return !moment(endDate as any).isBefore(moment(startDate as any), 'day');
        }),
});

const TrackFilterModal = ({ handleClose, onApply, defaultValues, viewMode = 'create' }: IProps) => {
    const { t } = useTranslation();
    // Timezone lives on basic.appearance, not on auth — reading state.auth.timezone silently
    // yields undefined and falls back to UTC, which shifts "today" a day for western zones.
    const userTimezone = useSelector((state: RootState) => state.basic.appearance?.timezone) || 'UTC';
    const trackParams = useSelector(trackParamsSelector);
    const { shipperOptions, consigneeOptions, handleSearchShipper, handleSearchConsignee } = useAddressDetails();
    // Same loader the table view's inline Origin/Destination fields use.
    const { isReady: isMapScript, isLoading: isScriptLoading } = useGoogleMapsScript();

    // ── Save View — same create/update functionality as the table view's Save View ──
    const { activeCustomView, isCreateViewEnable, viewNames, createView, updateView, isViewChangeLoading, isError: isViewNameError, setIsError: setIsViewNameError, getToday } = useTrackViews();
    const [isCreateViewOpen, setIsCreateViewOpen] = useState(false);

    const initialValues = useMemo(() => ({ ...DEFAULT_VALUES, ...defaultValues }), []);

    const { handleSubmit, control, setValue, reset, watch, getValues } = useForm<ITrackFilterForm>({
        resolver: yupResolver(validationSchema) as any,
        defaultValues: initialValues,
    });

    const startDate = watch('startDate');

    const watchedValues = watch();

    const isChanged = useMemo(() => {
        // Text fields compare TRIMMED (and with inner runs collapsed, like the search boxes):
        // spaces alone are not a filter, so they must not arm Apply Filters — and "abc " must
        // not read as a change from "abc" either.
        const normalize = (v: Partial<ITrackFilterForm>) => ({
            refSearch: normalizeTrackSearchQuery(v.refSearch || ''),
            shipperId: v.shipperId ?? null,
            consigneeId: v.consigneeId ?? null,
            status: v.status ?? null,
            serviceType: v.serviceType && v.serviceType !== 'all' ? v.serviceType : null,
            startDate: v.startDate ? moment(v.startDate as any).format('YYYY-MM-DD') : '',
            endDate: v.endDate ? moment(v.endDate as any).format('YYYY-MM-DD') : '',
            originSearch: normalizeTrackSearchQuery(v.originSearch || ''),
            destinationSearch: normalizeTrackSearchQuery(v.destinationSearch || ''),
        });

        return JSON.stringify(normalize(watchedValues)) !== JSON.stringify(normalize(initialValues));
    }, [watchedValues, initialValues]);

    // Matches wal-frontend-v2's AdvanceFilterModal handleReset: Reset CLEARS the form to
    // an unfiltered baseline rather than reverting to the currently-applied values. Stage is
    // preserved (it is the view the user is on, not a filter they applied here) and the date
    // range falls back to today, which is the Track default and reads as "no date filter".
    const handleReset = () => {
        const today = moment().tz(userTimezone).format('YYYY-MM-DD');

        reset({
            ...DEFAULT_VALUES,
            status: getValues('status'),
            startDate: createDateWithoutTimezoneConversion(moment(today)) ?? null,
            endDate: createDateWithoutTimezoneConversion(moment(today)) ?? null,
        });
    };

    // The Apply payload: form values with dates serialised the way onApply expects.
    const buildApplyPayload = (data: ITrackFilterForm): ITrackFilterForm => ({
        ...data,
        // Same normalization the dirty-check uses, so what gets applied is what was compared —
        // otherwise a trailing space would survive into the query and into a saved view.
        refSearch: normalizeTrackSearchQuery(data.refSearch || ''),
        originSearch: normalizeTrackSearchQuery(data.originSearch || ''),
        destinationSearch: normalizeTrackSearchQuery(data.destinationSearch || ''),
        startDate: data.startDate ? moment(data.startDate as any).format('YYYY-MM-DD 00:00:00') : '',
        endDate: data.endDate ? moment(data.endDate as any).format('YYYY-MM-DD 00:00:00') : '',
    });

    const onSubmit = (data: any) => {
        onApply(buildApplyPayload(data));
        handleClose();
    };

    const handleFormSubmit = handleSubmit(onSubmit);

    // What a Save View from here persists: the live params (search, sorting, division,
    // billing account are managed outside this modal) overlaid with the modal's PENDING
    // values — so the saved view is what the user sees in the form, not what was applied last.
    const candidateParams = useMemo<Partial<ITrackParams>>(
        () => ({
            ...trackParams,
            refSearch: normalizeTrackSearchQuery(watchedValues.refSearch || ''),
            shipperId: watchedValues.shipperId ?? null,
            consigneeId: watchedValues.consigneeId ?? null,
            status: watchedValues.status ?? trackParams.status,
            serviceType: watchedValues.serviceType ?? 'all',
            startDate: watchedValues.startDate ? moment(watchedValues.startDate as any).format('YYYY-MM-DD') : trackParams.startDate,
            endDate: watchedValues.endDate ? moment(watchedValues.endDate as any).format('YYYY-MM-DD') : trackParams.endDate,
            originSearch: normalizeTrackSearchQuery(watchedValues.originSearch || ''),
            destinationSearch: normalizeTrackSearchQuery(watchedValues.destinationSearch || ''),
        }),
        [trackParams, watchedValues]
    );

    // Effective mode: 'edit' intent always updates the active view; 'create' intent creates
    // while the view cap allows and falls back to updating the active view once all slots
    // are used (no button at all when there is nothing to update either).
    const isUpdateMode = viewMode === 'edit' ? !!activeCustomView : !isCreateViewEnable && !!activeCustomView;
    const isSaveViewVisible = isUpdateMode || (viewMode === 'create' && isCreateViewEnable);

    // Enable rule follows the mode: update → dirty vs the view's saved fields; create →
    // dirty vs the clean defaults for the pending stage.
    const isSaveViewEnable = useMemo(() => {
        const baseline = isUpdateMode && activeCustomView ? activeCustomView : buildAppliedTrackView(makeDefaultTrackViewParamsPatch(candidateParams.status ?? trackParams.status, getToday()));

        return isTrackViewChanged(baseline, candidateParams);
    }, [isUpdateMode, activeCustomView, candidateParams, trackParams.status, getToday]);

    // Create: the hook saves + selects the view; then the pending filters are applied so the
    // page immediately shows the new view's data (same as Apply Filters), and we close.
    const handleCreateViewSave = async (data: { name: string }) => {
        const created = await createView(data?.name, candidateParams);

        if (!created) return;

        setIsCreateViewOpen(false);
        onApply(buildApplyPayload(getValues()));
        handleClose();
    };

    // Update: the hook overwrites the active view with the pending fields and re-applies the
    // saved params itself, so no separate onApply is needed here.
    const handleUpdateActiveView = async () => {
        if (!activeCustomView) return;

        const updated = await updateView(activeCustomView, { params: candidateParams });

        if (updated) handleClose();
    };

    const handleStartDateChange = (onChange: any) => (e: any) => {
        const { value } = e;
        onChange(value);

        const currentEndDate = getValues('endDate');

        if (currentEndDate && value && moment(currentEndDate as any).isBefore(moment(value), 'day')) {
            setValue('endDate', null);
        }
    };

    const handleAddressInputChange = useCallback(
        (field: 'originSearch' | 'destinationSearch') => (value: string) => {
            setValue(field, value);
        },
        [setValue]
    );

    const handleAutocompleteChange = useCallback(
        (field: 'originSearch' | 'destinationSearch') => (value: any) => {
            if (!value || value.type === 'blur' || (value.target && value.type)) {
                return;
            }

            if (typeof value === 'object' && !value.target && value.fullAddress !== undefined) {
                setValue(field, value.fullAddress || '');
            }
        },
        [setValue]
    );

    return (
        <>
            <CommonModal
                title="Filters"
                handleClose={handleClose}
                size="max-w-[600px] w-full"
                primaryBtnText="Apply Filters"
                primaryBtnOnClick={handleFormSubmit}
                primaryBtnDisabled={!isChanged}
                secondaryBtnText="Reset"
                secondaryBtnOnClick={handleReset}
                secondaryBtnColor="btn_secondary_black"
                modalFooterCustomContent={
                    // Bottom-left button (mr-auto pushes Reset / Apply right). Mode-driven: update →
                    // "Edit View" saves the active view directly (no name popup); create → "Save View"
                    // opens the name popup. Hidden when create intent hits the cap with no active view.
                    isSaveViewVisible ? (
                        <ButtonCmp
                            className="btn_secondary_black mr-auto"
                            onClick={() => (isUpdateMode ? handleUpdateActiveView() : setIsCreateViewOpen(true))}
                            loading={isViewChangeLoading && !isCreateViewOpen}
                            disabled={!isSaveViewEnable || isViewChangeLoading}
                        >
                            {isUpdateMode ? t('QuotingHub.QuotingDashboard.Edit_View') : t('Common.Save_View')}
                        </ButtonCmp>
                    ) : undefined
                }
            >
                <form className="p-4 flex flex-col gap-y-3" onSubmit={handleFormSubmit}>
                    <div className="flex flex-col rounded-lg gap-3 border-utilityGray200 bg-gray25 p-3 border">
                        {/* REF/PRO#/BOL */}
                        <Controller
                            name="refSearch"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <InputText
                                    label="REF/PRO#/BOL"
                                    inputName="refSearch"
                                    placeholder="Search by reference, PRO, or BOL number"
                                    className="form_control"
                                    labelClassName="mb-1.5 block"
                                    onChangeFunc={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                                    value={value}
                                />
                            )}
                        />

                        {/* Shipper + Consignee */}
                        <div className="grid grid-cols-2 gap-x-3">
                            <Controller
                                name="shipperId"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <SelectBox
                                        label="Select Shipper"
                                        name="shipperId"
                                        id="shipperId"
                                        labelClassName="form_label mb-1.5 block"
                                        options={shipperOptions}
                                        placeholder="All shipper"
                                        isClearable
                                        isSearchable
                                        value={shipperOptions?.filter((o: any) => o.value === value) ?? null}
                                        onChangeFunc={(e) => onChange((e as IListOption)?.value ?? null)}
                                        onInputChange={(inputValue: string, { action }: any) => {
                                            if (action === 'input-change' || action === 'input-blur') {
                                                handleSearchShipper(inputValue);
                                            }
                                        }}
                                    />
                                )}
                            />
                            <Controller
                                name="consigneeId"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <SelectBox
                                        label="Select Consignee"
                                        name="consigneeId"
                                        id="consigneeId"
                                        options={consigneeOptions}
                                        labelClassName="form_label mb-1.5 block"
                                        placeholder="All consignee"
                                        isClearable
                                        isSearchable
                                        value={consigneeOptions?.filter((o: any) => o.value === value) ?? null}
                                        onChangeFunc={(e) => onChange((e as IListOption)?.value ?? null)}
                                        onInputChange={(inputValue: string, { action }: any) => {
                                            if (action === 'input-change' || action === 'input-blur') {
                                                handleSearchConsignee(inputValue);
                                            }
                                        }}
                                    />
                                )}
                            />
                        </div>

                        {/* Stage + Service Type */}
                        <div className="grid grid-cols-2 gap-x-3">
                            <Controller
                                name="status"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <SelectBox
                                        label="Stage"
                                        name="status"
                                        id="status"
                                        placeholder="Select status"
                                        options={STAGE_OPTIONS}
                                        labelClassName="form_label mb-1.5 block"
                                        isClearable={false}
                                        isSearchable={false}
                                        value={STAGE_OPTIONS.find((o) => o.value === value) ?? null}
                                        onChangeFunc={(e) => onChange((e as IListOption)?.value ?? null)}
                                    />
                                )}
                            />
                            <Controller
                                name="serviceType"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <SelectBox
                                        label="Service Type"
                                        name="serviceType"
                                        id="serviceType"
                                        labelClassName="form_label mb-1.5 block"
                                        placeholder="Select service type"
                                        options={SERVICE_TYPE_OPTIONS}
                                        isClearable
                                        isSearchable={false}
                                        value={SERVICE_TYPE_OPTIONS.find((o) => o.value === value) ?? null}
                                        onChangeFunc={(e) => onChange((e as IListOption)?.value ?? null)}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    {/* Date Range */}
                    <div className="flex flex-col rounded-lg gap-3 border-utilityGray200 bg-gray25 p-3 border">
                        <div className="grid grid-cols-2 gap-x-3">
                            <Controller
                                name="startDate"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <DateSelect
                                        label="From"
                                        inputName="startDate"
                                        placeholder="Select date"
                                        className="form_control"
                                        labelClassName="form_label block mb-1.5"
                                        dateFormat="dddd, MMMM Do, YYYY"
                                        selected={value}
                                        value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                                        onChangeFunc={handleStartDateChange(onChange)}
                                    />
                                )}
                            />
                            <Controller
                                name="endDate"
                                control={control}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <DateSelect
                                        label="To"
                                        inputName="endDate"
                                        placeholder="Select date"
                                        labelClassName="form_label block mb-1.5"
                                        className="form_control"
                                        dateFormat="dddd, MMMM Do, YYYY"
                                        minDate={startDate ? createDateWithoutTimezoneConversion(moment(startDate as any)) : undefined}
                                        selected={value}
                                        value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                                        onChangeFunc={(e: any) => onChange(e.value)}
                                        errorText={error?.message}
                                    />
                                )}
                            />
                        </div>

                        {/* Origin + Destination — city/state Google autocomplete, same as WAL */}
                        {isMapScript && (
                            <div className="grid grid-cols-2 gap-x-3">
                                <Controller
                                    name="originSearch"
                                    control={control}
                                    render={({ field }) => (
                                        <AutoCompleteGoogle
                                            inputName="originSearch"
                                            mapParentClass="w-full"
                                            label="Origin"
                                            placeholder="City or state"
                                            className="form_control pr-6 truncate"
                                            value={field.value}
                                            isStrictCityStateOnly
                                            isClearable
                                            disabled={isScriptLoading}
                                            onChangeFunc={handleAutocompleteChange('originSearch')}
                                            onAddressInputChange={handleAddressInputChange('originSearch')}
                                            onBlur={handleAutocompleteChange('originSearch')}
                                        />
                                    )}
                                />
                                <Controller
                                    name="destinationSearch"
                                    control={control}
                                    render={({ field }) => (
                                        <AutoCompleteGoogle
                                            inputName="destinationSearch"
                                            mapParentClass="w-full"
                                            label="Destination"
                                            placeholder="City or state"
                                            className="form_control pr-6 truncate"
                                            value={field.value}
                                            isStrictCityStateOnly
                                            isClearable
                                            disabled={isScriptLoading}
                                            onChangeFunc={handleAutocompleteChange('destinationSearch')}
                                            onAddressInputChange={handleAddressInputChange('destinationSearch')}
                                            onBlur={handleAutocompleteChange('destinationSearch')}
                                        />
                                    )}
                                />
                            </div>
                        )}
                    </div>
                </form>
            </CommonModal>

            {/* Name popup for Save View. Rendered as a sibling of the filter modal (not inside its
                scrollable body) so it stacks on top, exactly like the table view renders it. */}
            {isCreateViewOpen && (
                <CreateViewModal
                    title={t('QuotingHub.QuotingDashboard.Create_New_View')}
                    primaryBtnText={t('QuotingHub.QuotingDashboard.Create_View')}
                    handleClose={() => {
                        setIsCreateViewOpen(false);
                        setIsViewNameError(false);
                    }}
                    handleSave={handleCreateViewSave}
                    isLoading={isViewChangeLoading}
                    viewNames={viewNames}
                    modalData={null}
                    isError={isViewNameError}
                    setIsError={setIsViewNameError}
                />
            )}
        </>
    );
};

export default TrackFilterModal;
